import React, { useState, useEffect, useCallback } from 'react';
import { KitchenStaffLayout } from '../../components/staff/KitchenStaffLayout';
import { ClipboardList, Clock, Package, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const StaffDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;
      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      const response = await axios.get(`${BACKEND_URL}/api/orders/property/${propertyId}`);
      if (response.data.code === 200) {
        setOrders(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const countByStatus = (status) => orders.filter(o => o.status === status).length;
  const cancelledCount = orders.filter(o => o.status.startsWith('Cancelled')).length;

  const stats = [
    { label: 'Total Orders', value: orders.length, icon: ClipboardList, color: 'from-blue-500 to-blue-600' },
    { label: 'Order Placed', value: countByStatus('Order Placed'), icon: Clock, color: 'from-amber-500 to-amber-600' },
    { label: 'In Preparation', value: countByStatus('In Preparation'), icon: Package, color: 'from-purple-500 to-purple-600' },
    { label: 'Delivered', value: countByStatus('Delivered'), icon: CheckCircle, color: 'from-green-500 to-green-600' },
    { label: 'Cancelled', value: cancelledCount, icon: XCircle, color: 'from-red-500 to-red-600' },
  ];

  const recentOrders = orders.slice(0, 10);

  if (loading) {
    return (
      <KitchenStaffLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </KitchenStaffLayout>
    );
  }

  return (
    <KitchenStaffLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Overview of today's orders</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className={`bg-gradient-to-br ${stat.color} rounded-xl shadow-md p-5 text-white`}>
                <div className="flex items-center justify-between mb-2">
                  <Icon className="w-7 h-7 opacity-80" />
                </div>
                <p className="text-sm opacity-90">{stat.label}</p>
                <p className="text-3xl font-bold">{stat.value}</p>
              </div>
            );
          })}
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Recent Orders</h2>
          {recentOrders.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No orders yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className="pb-3 font-medium text-slate-600">Guest</th>
                    <th className="pb-3 font-medium text-slate-600">Room</th>
                    <th className="pb-3 font-medium text-slate-600">Items</th>
                    <th className="pb-3 font-medium text-slate-600">Total</th>
                    <th className="pb-3 font-medium text-slate-600">Status</th>
                    <th className="pb-3 font-medium text-slate-600">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {recentOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="py-3 text-slate-800 font-medium">{order.guestName || 'Guest'}</td>
                      <td className="py-3 text-slate-600">{order.roomNumber || '—'}</td>
                      <td className="py-3 text-slate-600">{order.items?.length || 0} items</td>
                      <td className="py-3 text-slate-800 font-medium">${order.totalAmount?.toFixed(2)}</td>
                      <td className="py-3">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          order.status === 'Order Placed' ? 'bg-blue-100 text-blue-700' :
                          order.status === 'Order Accepted' ? 'bg-indigo-100 text-indigo-700' :
                          order.status === 'In Preparation' ? 'bg-amber-100 text-amber-700' :
                          order.status === 'Ready to Pick' ? 'bg-purple-100 text-purple-700' :
                          order.status === 'Picked Up' ? 'bg-cyan-100 text-cyan-700' :
                          order.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                          order.status.startsWith('Cancelled') ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="py-3 text-slate-500 text-xs">
                        {new Date(order.createdAt).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </KitchenStaffLayout>
  );
};

export default StaffDashboard;
